import React, { useMemo, useState } from "react";
import InputField from "../InputField";
import Dropdown from "../Dropdown";
import MailboxWishesSection from "./MailboxWishesSection";
import { defaultDeletionDate, userOptionLabel } from "./offboardingFormData";

/**
 * The offboarding form: who is leaving, when, and what should happen to the computer
 * and the mailbox.
 *
 * The component is controlled — it never mutates `value`, every change is reported as a
 * new object through `onChange`.
 *
 * @param {object} props
 * @param {object[]} props.users - The customer's users; deleted ones are filtered out
 * @param {object} props.settings - SCO form settings for the customer
 * @param {object} props.value - The current offboarding form value
 * @param {(next: object) => void} props.onChange - Receives the updated form value
 * @returns {JSX.Element} The offboarding form
 */
const OffboardingForm = ({ users, settings, value, onChange }) => {
  // The suggested deletion date must not overwrite a date the orderer has chosen, but it
  // should keep following the end date until they do.
  const [deletionDateTouched, setDeletionDateTouched] = useState(false);

  const userOptions = useMemo(
    () =>
      (users || [])
        .filter((user) => !user.isDeleted)
        .map((user) => ({
          userPublicId: user.userPublicId,
          label: userOptionLabel(user),
        })),
    [users]
  );

  // The person leaving must never be offered as their own mailbox delegate or forward
  // target: the mailbox would be handed to an account that is about to be deleted.
  const otherUserOptions = useMemo(
    () =>
      userOptions.filter(
        (option) => option.userPublicId !== value.userPublicId
      ),
    [userOptions, value.userPublicId]
  );

  const selectedUserLabel =
    userOptions.find((option) => option.userPublicId === value.userPublicId)
      ?.label || "";

  const applyChanges = (changes) => onChange({ ...value, ...changes });

  const handleUserSelect = (label) => {
    const chosen = userOptions.find((option) => option.label === label);
    const userPublicId = chosen ? chosen.userPublicId : "";

    // Mailbox wishes chosen earlier may point at the person now being offboarded.
    applyChanges({
      userPublicId,
      sharedMailboxAccessUserPublicIds: (
        value.sharedMailboxAccessUserPublicIds || []
      ).filter((publicId) => publicId !== userPublicId),
      forwardToUserPublicId:
        value.forwardToUserPublicId === userPublicId
          ? ""
          : value.forwardToUserPublicId,
    });
  };

  const handleEndDateChange = (endDate) =>
    applyChanges({
      endDate,
      deletionDate: deletionDateTouched
        ? value.deletionDate
        : defaultDeletionDate(
            endDate,
            settings.offboardingDeletionDefaultDays
          ),
    });

  const handleDeletionDateChange = (deletionDate) => {
    setDeletionDateTouched(true);
    applyChanges({ deletionDate });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
      <div className="flex flex-col gap-4">
        <Dropdown
          label="User"
          value={selectedUserLabel}
          options={userOptions.map((option) => option.label)}
          onSelect={handleUserSelect}
        />

        <InputField
          label="End date"
          value={value.endDate}
          onChange={(event) => handleEndDateChange(event.target.value)}
          type="date"
        />

        <div>
          <InputField
            label="Deletion date"
            value={value.deletionDate}
            onChange={(event) => handleDeletionDateChange(event.target.value)}
            type="date"
          />
          {settings.offboardingHelpText && (
            <p className="text-sm text-gray-600 mt-1">
              {settings.offboardingHelpText}
            </p>
          )}
        </div>

        <InputField
          label="What should happen to the existing computer?"
          value={value.computerDisposition}
          onChange={(event) =>
            applyChanges({ computerDisposition: event.target.value })
          }
          placeholder="For example returned, wiped or handed over"
        />
      </div>

      <div className="flex flex-col gap-4">
        <MailboxWishesSection
          value={value}
          otherUserOptions={otherUserOptions}
          onFieldsChange={applyChanges}
        />

        <InputField
          label="Comment"
          value={value.comment}
          onChange={(event) => applyChanges({ comment: event.target.value })}
          placeholder="Anything else Diflex should know"
          type="text-area"
        />
      </div>
    </div>
  );
};

export default OffboardingForm;
